/**
 * permissions.ts — Single source of truth for all RBAC permission strings.
 *
 * Every permission follows the pattern: `domain:action`
 * This file is pure constants — no React, no side effects.
 *
 * ⚠️  If you add a permission here, you MUST also add it to the role map in roles.ts
 *     and update the tests in __tests__/rbac/permissions.spec.ts.
 */

// ─── Role Type ───────────────────────────────────────────────────────────────
export const ROLES = {
  OWNER: 'OWNER',
  MANAGER: 'MANAGER',
  CASHIER: 'CASHIER',
  ACCOUNTANT: 'ACCOUNTANT',
  INVENTORY_MANAGER: 'INVENTORY_MANAGER',
  VIEWER: 'VIEWER',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

/** Type guard — validates a string is a known Role */
export function isValidRole(value: string): value is Role {
  return Object.values(ROLES).includes(value as Role);
}

// ─── Permission Constants ────────────────────────────────────────────────────

// Dashboard
export const DASHBOARD_VIEW = 'dashboard:view';

// Billing / POS
export const BILLING_CREATE = 'billing:create';
export const BILLING_VIEW = 'billing:view';
export const BILLING_VOID = 'billing:void';
export const BILLING_CREDIT_NOTE = 'billing:credit_note';
/**
 * Quick Bill — typed items and a hand-entered bill date (any day, past or
 * future). The date moves a real GST sale between days and return periods, so
 * it is an owner/manager decision, not a till default.
 */
export const BILLING_QUICK_BILL = 'billing:quick_bill';

// Invoice History
export const INVOICE_VIEW_ALL = 'invoice:view_all';
export const INVOICE_VIEW_OWN = 'invoice:view_own';

// Customers
export const CUSTOMER_VIEW = 'customer:view';
export const CUSTOMER_CREATE = 'customer:create';
export const CUSTOMER_EDIT = 'customer:edit';
export const CUSTOMER_STATEMENT = 'customer:statement';

// Inventory
export const INVENTORY_VIEW = 'inventory:view';
export const INVENTORY_CREATE = 'inventory:create';
export const INVENTORY_EDIT = 'inventory:edit';
export const INVENTORY_ADJUST_STOCK = 'inventory:adjust_stock';
export const INVENTORY_TRANSFER = 'inventory:transfer';
export const INVENTORY_IMPORT = 'inventory:import';

// Purchasing
export const PURCHASING_VIEW = 'purchasing:view';
export const PURCHASING_CREATE_PO = 'purchasing:create_po';
export const PURCHASING_RECEIVE_PO = 'purchasing:receive_po';
export const PURCHASING_SUPPLIER_MANAGE = 'purchasing:supplier_manage';
export const PURCHASING_RECORD_PAYMENT = 'purchasing:record_payment';
export const PURCHASING_CREATE_RETURN = 'purchasing:create_return';

// Reports
export const REPORTS_VIEW = 'reports:view';
export const REPORTS_DAY_CLOSE = 'reports:day_close';
export const REPORTS_GST = 'reports:gst';
export const REPORTS_EXPORT = 'reports:export';

// Settings & Staff
export const SETTINGS_MANAGE = 'settings:manage';
export const SETTINGS_STAFF_MANAGE = 'settings:staff_manage';

// Activity Log — Owner/Manager oversight of all number-bearing transactions
export const ACTIVITY_LOG_VIEW = 'activity:view';

// Commission
export const STAFF_COMMISSION_VIEW = 'staff:commission_view';
export const STAFF_COMMISSION_PAYOUT = 'staff:commission_payout';

// ─── Collected Set (for validation) ──────────────────────────────────────────
export const ALL_PERMISSIONS = [
  DASHBOARD_VIEW,
  BILLING_CREATE,
  BILLING_VIEW,
  BILLING_VOID,
  BILLING_CREDIT_NOTE,
  BILLING_QUICK_BILL,
  INVOICE_VIEW_ALL,
  INVOICE_VIEW_OWN,
  CUSTOMER_VIEW,
  CUSTOMER_CREATE,
  CUSTOMER_EDIT,
  CUSTOMER_STATEMENT,
  INVENTORY_VIEW,
  INVENTORY_CREATE,
  INVENTORY_EDIT,
  INVENTORY_ADJUST_STOCK,
  INVENTORY_TRANSFER,
  INVENTORY_IMPORT,
  PURCHASING_VIEW,
  PURCHASING_CREATE_PO,
  PURCHASING_RECEIVE_PO,
  PURCHASING_SUPPLIER_MANAGE,
  PURCHASING_RECORD_PAYMENT,
  PURCHASING_CREATE_RETURN,
  REPORTS_VIEW,
  REPORTS_DAY_CLOSE,
  REPORTS_GST,
  REPORTS_EXPORT,
  SETTINGS_MANAGE,
  SETTINGS_STAFF_MANAGE,
  ACTIVITY_LOG_VIEW,
  STAFF_COMMISSION_VIEW,
  STAFF_COMMISSION_PAYOUT,
] as const;

export type Permission = (typeof ALL_PERMISSIONS)[number];
